import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Component, LOCALE_ID, Inject, ViewChild, AfterViewInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ApiAboutService, VotingCenter } from '../Services/api-about.service';
import { ConfirmFeatureSetField, DateFeatureSetField, EmptyFeatureSetResponse, FeatureSetField } from '../backend-types/feature-set';
import { csrfTokenHeaderName, SessionService, VerifyForm } from '../Services/session.service';
import { environment } from '../../environments/environment';
import * as isEmail from 'email-validator';
import { CustomValidators } from '../custom-validators/custom-validators';
import { SearchCountryField } from '@justin-s/ngx-intl-tel-input';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { BallotListService } from '../ballot-list/ballot-list.service';
import { MatDialogRef } from '@angular/material/dialog';
import { ScreenOrderService } from '../Services/screen-order.service';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { SessionTimeoutModalComponent } from '../session-timeout-modal/session-timeout-modal.component';
import { InvisibleReCaptchaComponent } from 'ngx-captcha';
import { ApiService } from '../api/api.service';
import { passwordRoute, scanMfaQRroute, verifyMFARoute } from '../route-names';
import { SessionStorageService } from '../Services/session-storage.service';
import { defaultSupportEmail } from '../app.component';
import { DialogService } from '../Services/dialog.service';
import { DialogType } from '../Services/dialog.service';
import { ElectorDataService, MfaMethod, UserType } from '../Services/elector-data.service';

export const maxEmailLength = 254;   // From RFC 3696, erratum 1690.
export const maxPINLength = 16;

@Component({
    selector: 'app-verify-voter',
    templateUrl: './verify-voter.component.html',
    styleUrls: ['./verify-voter.component.scss'],
    standalone: false
})

/*
  TODO's: 
  1. Add error messages underneath input boxes 
  2. Convert control data to list of key value pairs and send to backend.
  3. Add custom validators for email, add confirm email
  4. Reformat to center, or have two items per row
  5. Add date dropdown
  6. Add previously supported special text boxes
*/

export class VerifyVoterComponent implements AfterViewInit {

  @ViewChild(DatePickerComponent) datePickerComponent!: DatePickerComponent;
  dialogRef: MatDialogRef<SessionTimeoutModalComponent> | undefined;
  @ViewChild('captchaRef') captchaComponent!: InvisibleReCaptchaComponent;

  verificationForm: FormGroup = new FormGroup({});
  fields: Array<FeatureSetField | ConfirmFeatureSetField | DateFeatureSetField> = [];

  dateField?: DateFeatureSetField;
  defaultDateParts: Set<"day" | "month" | "year"> = new Set(["day", "month", "year"]);
  removedDateFields: FeatureSetField[] = [];

  submitted = false;
  recaptchaResponse = '';
  readonly recaptchaSiteKey: string = environment.recaptchaSiteKey;
  country: string;
  SearchCountryField = SearchCountryField;
  readonly maxEmailLength: number = maxEmailLength;
  readonly maxPINLength: number = maxPINLength;
  alreadyVotedTime = "";
  readonly identityError = new BehaviorSubject(false);
  readonly validationError = new BehaviorSubject(false);
  displaySpinner = false;
  routeName = ""
  errorText?: string;
  errorTitle?: string;
  hide = true;
  electionOpen: boolean = true;
  readonly electionStartTime: Date;
  readonly electionEndTime: Date;
  locations: VotingCenter[] = [];
  
  supportEmail = defaultSupportEmail;

  constructor(
    readonly aboutService: ApiAboutService,
    private readonly fb: FormBuilder,
    public readonly announcer: LiveAnnouncer,
    @Inject(LOCALE_ID) readonly locale: string,
    private readonly router: Router,
    private readonly ballotListService: BallotListService,
    private readonly screenOrderService: ScreenOrderService,
    readonly sessionService: SessionService,
    private readonly apiService: ApiService,
    private storer: SessionStorageService,
    private dialogService: DialogService,
    private readonly userService: ElectorDataService
  ) {
    if(this.aboutService.electionNotYetOpen){ 
      this.errorTitle = $localize`Election Not Open Yet`;
      this.errorText = $localize`The election will open at ${this.aboutService.startTime} ${this.aboutService.localTimeZone}.`;
      this.dialogService.openDialog(DialogType.ALERT, this.errorTitle, this.errorText);
      this.electionOpen = false;
      this.router.navigate(environment.startRoute);
    } 
    if(this.aboutService.electionClosed){
      this.errorTitle = $localize`Election Closed`;
      this.errorText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime} ${this.aboutService.localTimeZone}.`;
      this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText);
      this.electionOpen = false;
      this.router.navigate(environment.startRoute);
    }
    if(this.sessionService.partiallyDeletedCookies()){
      try {
        let xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/endsession');
        xhr.withCredentials = true;
        if (this.sessionService.xsrfCookieName) {
          xhr.setRequestHeader(csrfTokenHeaderName, this.sessionService.xsrfCookieName);
        }
        xhr.send();
      } catch (e) {
        console.error("Error calling /endsession.")
      }
    } else {
      if (this.sessionService.activeSessionCheck()) {
        const errorTitle = $localize`Active Session`;
        const errorText = $localize`Someone is already logged in on this browser. Please close this tab and log in on another browser or another device.`;
        this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
      }
    }
    this.country = this.aboutService.aboutInformation.value.country
    if (this.aboutService.aboutInformation.value.org_feature_set === undefined) {
      this.fields = new EmptyFeatureSetResponse().featureSetFields.fields
    } else if(this.aboutService.VWAFlowType === 'GLOBAL') {
      this.fields = this.aboutService.aboutInformation.value.org_feature_set.featureSetFields.fields;
    } else {
      this.fields = this.aboutService.aboutInformation.value.org_feature_set.featureSetFields.fields.filter((field) => field.sendInOidvData)
    }
    if(this.aboutService.isVotingCenter() && this.aboutService.votingCenterList.length > 0){
      let votingCenter = this.aboutService.votingCenterObject;
      if(votingCenter) {
        this.locations = votingCenter.votingCenterList;
        this.fields.push(votingCenter.featureSetEntry);
      }
    }
    this.processFeatureSetFields(this.fields)

    this.verificationForm = new FormGroup({})
    this.addFormValidators()
    if(!this.aboutService.isVotingCenter()) {
      this.verificationForm.addControl('recaptcha', this.fb.control(''))
    }

    this.electionStartTime = new Date(2000, 1, 1);
    let start: string | undefined = this.aboutService.aboutInformation.value.org_feature_set.miscSettings?.electionStartTimestamp;
    if (start) {
      this.electionStartTime = new Date(start);
    }
    // I doubt anyone will use this after the year 100,000. 
    this.electionEndTime = new Date(100000, 1, 1);
    let expiry: string | undefined = this.aboutService.aboutInformation.value.org_feature_set.miscSettings?.electionEndTimestamp;
    if (expiry) {
      this.electionEndTime = new Date(expiry);
    }

    let returnOptions = this.aboutService.aboutInformation.value.org_feature_set.miscSettings?.ballotReturnOptions;
    if (returnOptions) {
      if (returnOptions.electronicOrPrintValue) {
        this.ballotListService.returnOptions.push(returnOptions.electronicOrPrintValue);
      }
      if (returnOptions.printValue) {
        this.ballotListService.returnOptions.push(returnOptions.printValue);
      }
    }
  }

  announce(text: string) {
    this.announcer.announce(text, "polite");
  }

  ngAfterViewInit() {
    this.announcer.announce($localize`Identity Verfication`, "assertive")
    if (document.readyState === "complete") {
      if(this.aboutService.isVotingCenter()) {
        this.resetFocus();
      } else {
        setTimeout(() => {
          //Fix for WAVE error *************************************************
          var response = document.getElementById("g-recaptcha-response");
          if (response) {
            response.setAttribute('aria-label', "Recaptcha response");
            response.setAttribute("aria-hidden", "true");
            response.setAttribute("aria-readonly", "true");
          }
          this.resetFocus();
        }, 200);
      }
    }

    const isDateFieldPresent = this.fields.find((field) => field.fieldType === 'date')
    if (isDateFieldPresent) {
      this.verificationForm.addControl(isDateFieldPresent.fieldName, this.datePickerComponent.formGroup);
      this.datePickerComponent.formGroup.setParent(this.verificationForm);
    }
    setTimeout(() => {
      this.routeName = this.router.url
    }, 1);

  }
  /*********************************************************************/
  resetFocus() {
    let focusField = document.getElementById('accessibility-button')
    let focusFieldMobile = document.getElementById('accessibility-button-mobile')

    if (focusField) {
      focusField.focus()
    }

    if (focusFieldMobile) {
      focusFieldMobile.focus()
    }
  }

  handleReset() {
    this.recaptchaResponse = ""
  }

  handleSuccess(e: any) {
    this.recaptchaResponse = e;
  }

  onTooltipClick(event: any) {
    event.preventDefault();
  }

  routeToMfaScreen(): boolean {
    return (this.aboutService.idUploadFlow || this.aboutService.vwaFlowType === 'ORG_1_STEP_MFA');
  }

  resolved(captchaResponse: string | null): void {

    if (captchaResponse === null) {
      console.error("Captcha response is null");
      return;
    }

    Promise.resolve().then(() => {
      this.verificationForm.patchValue({
        recaptcha: captchaResponse
      });
      this.onSubmitForm();
    }).catch(error => console.error("Error with captchaResponse:", error));
  }

  onSubmit() {
    if (this.sessionService.activeSessionCheck()) {
      const errorTitle = $localize`Active Session`;
      const errorText = $localize`Someone is already logged in on this browser. Please close this tab and log in on another browser or another device.`;
      this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
      this.captchaComponent.resetCaptcha();
      this.verificationForm.reset();
      return;
    }
    if(this.electionOpen) {
      if(this.aboutService.isVotingCenter()) {
        this.onSubmitForm();
      } else {
        this.captchaComponent.execute();
      }
    }
  }

  onSubmitForm() {
    this.submitted = true;
    let currentTime: Date = new Date(Date.now());
    if (!this.verificationForm.valid) {
      this.announcer.announce($localize`error Some fields are missing or invalid.`, "assertive");
    } else if (currentTime > this.electionEndTime) {
      const startTime = this.electionStartTime.toLocaleString(this.locale)
      const endTime =  this.electionEndTime.toLocaleString(this.locale)
      const title = $localize`Election closed` 
      const errorMessage = $localize`
              The election window occurred between: <br>
             ${ startTime} and ${endTime}.<br><br>
              The election has closed, so your verification cannot be processed.<br><br>
              If you believe this is in error, use the "Contact Support" link for assistance.`
      this.dialogService.openDialog(DialogType.ERROR, title, errorMessage);
    } else {
      this.displaySpinner = true
      const form: VerifyForm = { keyPairs: [] }
      const keys = Object.keys(this.verificationForm.controls)
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        if (key === 'recaptcha' || key.toLowerCase().startsWith("confirm_")) {
          continue
        }
        let value: string = this.verificationForm.get(key)?.value as string
        const field = this.fields.find((f) => f.fieldName === key)
        if ((field) && (field.fieldType === 'phone')) {
          if(this.verificationForm.get(key)?.value === null) {
            continue
          }
          value = this.verificationForm.get(key)?.value.e164Number;
        }
        if ((field) && (field.fieldType === 'date')) {
          if (this.removedDateFields.length > 0) {
            const dateTime = this.verificationForm.get(key)?.value
            this.removedDateFields.forEach((removed) => {
              switch (removed.fieldType) {
                case 'day':
                  value = String(dateTime['day']).padStart(2, '0')
                  break
                case 'month':
                  value = String(dateTime['month']).padStart(2, '0')
                  break
                case 'year':
                  value = String(dateTime['year']).padStart(4, '0')
                  break
              }
              const keyPair = { key: removed.fieldName, value: value }
              form.keyPairs.push(keyPair)
            })
            continue
          } else {
            let dateTime = this.verificationForm.get(key)?.value
            let dateString = String(dateTime['year']).padStart(4, '0') + '-' + String(dateTime['month']).padStart(2, '0') + '-' + String(dateTime['day']).padStart(2, '0')
            value = dateString
          }
        }
        const keyPair = { key: key, value: value }
        form.keyPairs.push(keyPair)
      }
      if(this.aboutService.idUploadFlow) {
        this.routeIDorMFA(form);
      } else if(this.aboutService.isIDVflow()) {
        this.routeIDorMFA(form);
      } else if(this.aboutService.vwaFlowType === 'ORG_1_STEP_MFA') { 
        this.routeIDorMFA(form);
      } else {
        this.routeCreateSession(form);
      }
    }
  }

  routeCreateSession(form: VerifyForm) {
    //This force error to test dialogs
      // (this.sessionService.create = () =>
      //   throwError({
      //     error_type: 'ALREADY_VOTED', 
      //     time: new Date().toISOString(),
      //   } as any)) as any;
      let recaptchaString: string = "";
      if (!this.aboutService.isVotingCenter()) {
        recaptchaString = this.verificationForm.get('recaptcha')?.value as string
      }
      (this.sessionService.create(form, recaptchaString, this.locale)).subscribe({
      next: (response) => {
        this.setVoterBallotType(response?.ballotType);
        this.ballotListService.setBallots(response);
        this.aboutService.signatureRequired.next(response?.events?.at(0)?.voteEventData?.optionalEventConfiguration?.submitSignature ?? false);
        this.userService.setIdvStatus(response?.elector);
      },
      error: (err) => {
        if(!this.aboutService.isVotingCenter()) {
          this.captchaComponent.resetCaptcha();
        }
        this.displaySpinner = false;
        if (
          (err.error_type !== undefined && err.error_type === 'ALREADY_VOTED') ||
          (err.error !== undefined && err.error.error_type === 'ALREADY_VOTED')
        ) {
          const date = new Date(err.time);
          const time = `${date.toLocaleTimeString(this.locale)}, ${date.toLocaleDateString(this.locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}`;          
          const emailUsedFlag: boolean = environment.useEmail ?? false;   
          const title = $localize`Ballot Submission`       
          let errorMessage: string = $localize`<div>
            <span>Our records indicate you submitted your ballot at ${time}.</span>`;
          if (emailUsedFlag) {
            errorMessage += $localize`
              <span>Please check your email for your confirmation receipt.</span>`;
          }          
          errorMessage += $localize`
            <span>If you believe this is an error, please 
              <a href="mailto:${this.supportEmail}?subject=Web App, Ballot Previously Submitted">contact us</a>.
            </span>
          </div>`;
          this.dialogService.openDialog(DialogType.ERROR, title, errorMessage);
        } else if ((err.error_type !== undefined && err.error_type === 'ALREADY_LOGGED_IN') ||
        (err.error !== undefined && err.error.error_type === 'ALREADY_LOGGED_IN')) {
          this.errorText = $localize`You already have an active session. To continue, please close the active session and try again.`;
          this.errorTitle = $localize`Already Logged In.`
          this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText );
        } else if ((err.error_type !== undefined && err.error_type === 'INVALID_IDENTITY') ||
          (err.error !== undefined && err.error.error_type === 'INVALID_IDENTITY')) {
          const title = $localize`Information not found`
          const errorMessage = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the <b>"Contact Support"</b> link for assistance.`
          this.dialogService.openDialog(DialogType.ERROR, title, errorMessage);
        } 
        else if ((err.error_type !== undefined && err.error_type === 'BALLOT_NOT_AVAILABLE') ||
          (err.error !== undefined && err.error.error_type === 'BALLOT_NOT_AVAILABLE')) {
          const title = $localize`Ballot not available`
          const errorMessage = $localize`<span>Your ballot isn’t available yet. Please check back later. If you believe this is an error, please <a href="mailto:${this.supportEmail}?subject=Web App, Ballot Not Available" i18n-href="mailto: subject line to be translated. Translate all text after 'subject='">contact us</a>.</span>`;
          this.dialogService.openDialog(DialogType.ALERT, title, errorMessage);
        }
        else if ((err.error_type !== undefined && err.error_type === 'DOCUMENT_NOT_FOUND') ||
          (err.error !== undefined && err.error.error_type === 'DOCUMENT_NOT_FOUND')) {
          const title = $localize`Ballot not available`
          const errorMessage = $localize`<span>Your ballot isn’t available yet. Please check back later. If you believe this is an error, please <a href="mailto:${this.supportEmail}?subject=Web App, Ballot Not Available" i18n-href="mailto: subject line to be translated. Translate all text after 'subject='">contact us</a>.</span>`;
          this.dialogService.openDialog(DialogType.ALERT, title, errorMessage);
        } else if( err.error.message?.includes("captcha") || err.message == 'Invalid recaptcha'){
          const errorText = $localize`Please disable pop-up blockers, try using another browser or another device, try again later, or use the "Contact Support" link for assistance.`;
          const errorTitle = $localize`Captcha Error.`
          this.dialogService.openDialog(DialogType.ALERT, errorTitle, errorText);
        }
        else if ((err.error_type !== undefined && (err.error_type === 'DECODER_STATE_LOST' || err.error_type === 'INVALID_MTE_DECODING')) ||
          (err.error !== undefined && (err.error.error_type === 'DECODER_STATE_LOST' || err.error.error_type === 'INVALID_MTE_DECODING' ))) {
          const errorText = $localize`We could not process your request. Please try to submit again or refresh the page.`;
          const errorTitle = $localize`Error.`
          this.dialogService.openDialog(DialogType.ALERT, errorTitle, errorText);
        }
         else if ((err.error_type !== undefined && err.error_type === 'ELECTION_NOT_YET_OPEN') ||
        (err.error !== undefined && err.error.error_type === 'ELECTION_NOT_YET_OPEN')) {
          const errorText = $localize`The election will be open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
          const errorTitle = $localize`Election not open yet`
          this.dialogService.openDialog(DialogType.OK, errorTitle, errorText);
        } 
        else if ((err.error_type !== undefined && err.error_type === 'ELECTION_CLOSED') ||
        (err.error !== undefined && err.error.error_type === 'ELECTION_CLOSED')) {
          const errorText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
          const errorTitle = $localize`Election Closed`
          this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
        } 
        else if ((err.error_type !== undefined && err.error_type === 'MISSING_EMAIL') ||
        (err.error !== undefined && err.error.error_type === 'MISSING_EMAIL')) {
          const errorText = $localize`We could not find an email address on file. Please use the "Contact Support" link for assistance.`;
          const errorTitle = $localize`Missing Email`
          this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
        } 
        else if ((err.error_type !== undefined && err.error_type === 'INVALID_FORM') ||
        (err.error !== undefined && err.error.error_type === 'INVALID_FORM')) {
          const errorText = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          const errorTitle = $localize`Malformed Data`
          this.dialogService.openDialog(DialogType.ALERT, errorTitle, errorText);
        } 
        else {
          console.error(err);
          const errorText = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          const errorTitle = $localize`Unknown Error`
          this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
        }
      },
      complete: () => {
        setTimeout(() => {
          this.displaySpinner = false
          this.router.navigate(this.screenOrderService.nextRoute('home'), {
            queryParams: { status: 'success' }
          })
          this.resetFocus();
        }, 500);

        this.validationError.next(false);
      },
    });
  }

  routeIDorMFA(form: VerifyForm) {
    let requestBody: any = {
      data: form.keyPairs,
      locale: this.locale,
      deviceProfile: this.storer.getDeviceProfile()
    };
    if(!this.aboutService.isVotingCenter()) {
      requestBody["g-recaptcha-response"] = this.verificationForm.get('recaptcha')?.value as string;
    }
    this.apiService.postWithHandshakeCheck<VerifyWithMfaResponse>('/api/onboard', requestBody).subscribe({
      next: (response) => {
        if(response.body) {
          this.userService.userType = response.body.user_type;
          switch(response.body.password_screen) {
            case 'NEW':
              this.router.navigate(passwordRoute, {state: {voterID: response.body?.voter_id, type: 'NEW'}});
              break;
            case 'CHECK':
              this.router.navigate(passwordRoute, {state: {voterID: response.body?.voter_id, type: 'CHECK'}});
              break;
            case 'MFA':
              this.userService.userAuthData.set({
                voter_id: response.body.voter_id,
                mfaMethod: response.body.mfa_method,
                mfaVerified: false,
                mfaExpiration: response.body.mfa_expiration,
                mfaExpirationTime: response.body.mfa_expiration_time,
                maxResends: response.body.max_resends,
                resendsRemaining: response.body.resends_remaining,
                resendExpiration: response.body.resend_expiration,
                resendExpirationTime: response.body.resend_expiration_time,
                maxAttempts: response.body.max_attempts,
                attemptsRemaining: response.body.attempts_remaining
              });
              if(response.body?.attempts_remaining == 0){
                const errorMessage = $localize`You have no more attempts to verify your MFA code remaining. Please use the "Contact Support" link for assistance.`
                const title = $localize`Maximum MFA Attempts Reached`;
                this.dialogService.openDialog( DialogType.ERROR, title, errorMessage );
                this.captchaComponent.resetCaptcha()
                this.displaySpinner = false;
              } else if(this.aboutService.vwaFlowType === 'ORG_1_STEP_MFA') {
                if(response.body.mfa_secret) {
                  this.userService.setAuthAppSecret(response.body.voter_id, response.body.mfa_secret);
                  this.router.navigate(scanMfaQRroute);
                } else {
                  this.router.navigate(verifyMFARoute, {state: {voterID: response.body.voter_id, type: 'MFA'}});
                }
              } else {
                this.router.navigate(verifyMFARoute, {state: {voterID: response.body.voter_id, type: 'MFA'}});
              }
              break;
            default:
              this.router.navigate(passwordRoute, {state: {voterID: response.body.voter_id, type: 'NEW'}});
              break;
          }
        } else {
          const errorMessage = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the "Contact Support" link for assistance.`
          const title = $localize`Information not found`;
          this.dialogService.openDialog( DialogType.ERROR, title , errorMessage );
          if(!this.aboutService.isVotingCenter()) {
            this.captchaComponent.resetCaptcha();
          }
          this.displaySpinner = false;
        }
      },
      error: (err) => {
        if(!this.aboutService.isVotingCenter()) {
          this.captchaComponent.resetCaptcha();
        }
        this.displaySpinner = false;
        if ((err.error_type !== undefined && err.error_type === 'ALREADY_VOTED') ||
        (err.error !== undefined && err.error.error_type === 'ALREADY_VOTED')) {
          const date = new Date(err.error.time);
          const time = `${date.toLocaleTimeString(this.locale)}, ${date.toLocaleDateString(this.locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}`;          
          const emailUsedFlag: boolean = environment.useEmail ?? false;   
          const title = $localize`Ballot Submission`       
          let errorMessage: string = $localize`<div>
            <span>Our records indicate you submitted your ballot at ${time}.</span>`;
          if (emailUsedFlag) {
            errorMessage += $localize`
              <span>Please check your email for your confirmation receipt.</span>`;
          }          
          errorMessage += $localize`
            <span>If you believe this is an error, please 
              <a href="mailto:${this.supportEmail}?subject=Web App, Ballot Previously Submitted">contact us</a>.
            </span>
          </div>`;
          this.dialogService.openDialog(DialogType.ERROR, title, errorMessage);
        } else if ((err.error_type !== undefined && err.error_type === 'ALREADY_LOGGED_IN') ||
        (err.error !== undefined && err.error.error_type === 'ALREADY_LOGGED_IN')) {
          this.errorText = $localize`You already have an active session. To continue, please close the active session and try again.`;
          this.errorTitle = $localize`Already Logged In.`
          this.dialogService.openDialog(DialogType.ERROR, this.errorTitle, this.errorText );
        } else if ((err.error_type !== undefined && err.error_type === 'INVALID_IDENTITY') ||
          (err.error !== undefined && err.error.error_type === 'INVALID_IDENTITY')) {
          const title = $localize`Information not found`;
          const errorMessage = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the "Contact Support" link for assistance.`
          this.dialogService.openDialog(DialogType.ERROR, title, errorMessage );
        } else if ((err.error_type !== undefined && err.error_type === 'BALLOT_NOT_AVAILABLE') ||
          (err.error !== undefined && err.error.error_type === 'BALLOT_NOT_AVAILABLE')) {
          const title = $localize`Ballot not available`
          const errorMessage = $localize`<span>Your ballot isn't available yet. Please check back later. If you believe this is an error, please <a href="mailto:${this.supportEmail}?subject=Web App, Ballot Not Available" i18n-href="mailto: subject line to be translated. Translate all text after 'subject='">contact us</a>.</span>`;
          this.dialogService.openDialog(DialogType.ALERT, title, errorMessage);
        }
        else if (err.message == 'Invalid recaptcha') {
          const errorTitle = $localize`Captcha error.`;
          const errorText = $localize`Please disable pop-up blockers or try using another browser or another device.`;
          this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
        } 
        else if ((err.error_type !== undefined && err.error_type === 'DECODER_STATE_LOST') ||
          (err.error !== undefined && err.error.error_type === 'DECODER_STATE_LOST')) {
          const errorText = $localize`Your session timed out. Please try to submit again or refresh page.`;
          const errorTitle = $localize`Registration Not Successful.`
          this.dialogService.openDialog(DialogType.ALERT, errorTitle, errorText);
        }
        else if ((err.error_type !== undefined && err.error_type === 'ELECTION_NOT_YET_OPEN') ||
        (err.error !== undefined && err.error.error_type === 'ELECTION_NOT_YET_OPEN')) {
          const errorText = $localize`The election will be open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
          const errorTitle = $localize`Election not open yet`
          this.dialogService.openDialog(DialogType.OK, errorTitle, errorText);
        } 
        else if ((err.error_type !== undefined && err.error_type === 'ELECTION_CLOSED') ||
        (err.error !== undefined && err.error.error_type === 'ELECTION_CLOSED')) {
          const errorText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
          const errorTitle = $localize`Election Closed`
          this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText); 
        }  
        else if ((err.error_type !== undefined && err.error_type === 'MISSING_EMAIL') ||
        (err.error !== undefined && err.error.error_type === 'MISSING_EMAIL')) {
          const errorText = $localize`We could not find an email address on file. Please use the "Contact Support" link for assistance.`;
          const errorTitle = $localize`Missing Email`
          this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
        } 
        else if ((err.error_type !== undefined && err.error_type === 'INVALID_FORM') ||
        (err.error !== undefined && err.error.error_type === 'INVALID_FORM')) {
          const errorText = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          const errorTitle = $localize`Malformed Data`
          this.dialogService.openDialog(DialogType.ALERT, errorTitle, errorText);
        } else if ((err.error_type !== undefined && err.error_type === 'MAX_OTP_ATTEMPTS') ||
        (err.error !== undefined && err.error.error_type === 'MAX_OTP_ATTEMPTS')) {
          const errorText = $localize`Your account has been temporarily blocked. To unblock it, please use the "Contact Support" link for assistance.`;
          const errorTitle = $localize`Maximum Attempts Reached.`
          this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
        } else if( err.error.message?.includes("captcha") || err.message == 'Invalid recaptcha'){
          const errorText = $localize`Please disable pop-up blockers, try using another browser or another device, try again later, or use the "Contact Support" link for assistance.`;
          const errorTitle = $localize`Captcha Error.`
          this.dialogService.openDialog(DialogType.ALERT, errorTitle, errorText);
        } else {
          console.error(err);
          const errorText = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          const errorTitle = $localize`Error`
          this.dialogService.openDialog(DialogType.ERROR, errorTitle, errorText);
        }
      },
      complete: () => {
        this.displaySpinner = false;
        this.validationError.next(false);
      }
    });

  }

  focusErrorDialog() {
    setTimeout(() => {
      const focusableElements = 'button';
      const modal: any = document.querySelector('#verification-error');
      const firstFocusableElement = modal.querySelectorAll(focusableElements)[0]
      firstFocusableElement.focus()
    }, 200);
  }

  setVoterBallotType(returnMethod: string | undefined) {
    switch (returnMethod) {
      case this.screenOrderService.electronicOrPrintString: {
        this.screenOrderService.voterBallotType = "ELECTRONICORPRINT";
        this.screenOrderService.returnMethodSelected = false;
        break;
      }
      case this.screenOrderService.printString: {
        this.screenOrderService.voterBallotType = "PRINT";
        this.screenOrderService.selectedReturnMethod = "PRINT";
        this.screenOrderService.returnMethodSelected = true;
        break;
      }
      default: {
        this.screenOrderService.voterBallotType = "ELECTRONIC";
        this.screenOrderService.selectedReturnMethod = "ELECTRONIC";
        this.screenOrderService.returnMethodSelected = true;
        break;
      }
    }
  }

  private processFeatureSetFields(fields: any[]): void {
    for (let i = fields.length - 1; i >= 0; i--) {
      const field = fields[i];
      if ((field.fieldType === 'email' || field.fieldType === 'phone') && !this.aboutService.idUploadFlow) {
        const newField = new ConfirmFeatureSetField(field);
        fields.splice(i + 1, 0, newField);
      }

      if (field.fieldType === 'day' || field.fieldType === 'month' || field.fieldType === 'year') {
        const dateField = fields.find((f) => f instanceof DateFeatureSetField);
        if (dateField) {
          (dateField as DateFeatureSetField).dateParts.add(field.fieldType);
          this.dateField = dateField as DateFeatureSetField;
          const removed = fields.splice(i, 1);
          this.removedDateFields = this.removedDateFields.concat(removed);
        } else {
          const newField = new DateFeatureSetField(field, new Set([field.fieldType]));
          this.dateField = newField;
          this.removedDateFields = fields.splice(i, 1, newField);
        }
      }
    }
  }

  private addFormValidators(): void {
    for (let field of this.fields) {
      let validators: ValidatorFn[] = [];
      if (field.isMandatory && field.fieldType != 'date') {
        validators.push(Validators.required);
      }
      if (field.isPin) {
        validators.push(Validators.maxLength(maxPINLength));
        validators.push(Validators.pattern('[a-zA-Z0-9]*'));
      }
      if (field.fieldType === 'email') {
        validators.push(Validators.maxLength(maxEmailLength));
        validators.push(email());
      }
      if ((field instanceof ConfirmFeatureSetField) && (field.fieldType === 'confirm_email')) {
        validators.push(email());
        validators.push(new CustomValidators(field.confirmingField).confirmEqual);
      }
      if ((field instanceof ConfirmFeatureSetField) && field.fieldType === 'confirm_phone') {
        validators.push(new CustomValidators(field.confirmingField).confirmPhoneMatch);
      }
      if(field.fieldType === 'voting_center') {
        let vcIdListRegex = "[" + this.aboutService.votingCenterList.map((vc) => vc.id).join("|") + "]";
        validators.push(Validators.pattern(vcIdListRegex));
      }
      if (field.fieldType != 'date') {
        this.verificationForm.addControl(field.fieldName, this.fb.control(null, validators));
      }
    }
    // One more pass to cross-check the confirming fields.
    for (let field of this.fields) {
      if (field.fieldType === 'email') {
        const confirmEmailField = this.fields.find(f => 
          f instanceof ConfirmFeatureSetField && 
          f.fieldType === 'confirm_email'
        );
        if (confirmEmailField) {
          this.verificationForm.get(field.fieldName)?.valueChanges.subscribe(() => {
            this.verificationForm.get(confirmEmailField.fieldName)?.updateValueAndValidity();
          });
        }
      }
      if (field.fieldType === 'phone') {
        const confirmPhoneField = this.fields.find(f => 
          f instanceof ConfirmFeatureSetField && 
          f.fieldType === 'confirm_phone'
        );
        if (confirmPhoneField) {
          this.verificationForm.get(field.fieldName)?.valueChanges.subscribe(() => {
            this.verificationForm.get(confirmPhoneField.fieldName)?.updateValueAndValidity();
          });
        }
      }
    }
  }

  // Trim text pasted into an input field.
  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
  
    const trimmedText = event.clipboardData?.getData('text').trim();
    const target = event.target as HTMLInputElement;
    const controlName = target.getAttribute('id');
    
    if (controlName) {
      this.verificationForm.get(controlName)?.setValue(trimmedText);
    }
  }

}

/** Custom email address validator
 *
 * We use this rather than the default Angular validation because it
 * matches what is on the server.
 * */
export function email(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null =>
    (control.value === null || isEmail.validate(control.value)) ? null :
      { invalidEmail: { value: control.value } };
}

export interface VerifyWithMfaResponse {
  voter_id: string,
  password_screen: string,
  user_type: UserType,
  mfa_secret?: string,
  mfa_method?: MfaMethod,
  mfa_expiration?: number,
  mfa_expiration_time?: number,
  max_resends?: number,
  resends_remaining?: number,
  resend_expiration?: number,
  resend_expiration_time?: number,
  max_attempts?: number,
  attempts_remaining?: number
  new_user?: boolean;
};